/**
 * Route-scoped provider for the Kelly AI surface. Hosts the single conversation controller
 * (useChatConversations) once and exposes it through two contexts so the streaming conversation surface and
 * the collapsed rail read from the same source without the rail re-rendering per streamed token:
 *   - KellyChatContext  — the full controller, consumed by the conversation surface.
 *   - KellyPanelContext — a memoised slice (thread list + select/new + load flags) for the collapsed rail
 *                         and its select-only hover preview (APP-3397); its deps EXCLUDE messages/liveMessages,
 *                         so its value ref is stable while a turn streams.
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react"
import type { ComponentType, ReactNode } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useAccountAlias } from "resources/account"
import { getModel } from "modules/home-layout/selectors/model"
import { consumeComposerSeed, selectPendingComposerSeed } from "../store/agent-chat-slice"
import type { Thread } from "connected-components/ai-teammate/types/thread"
import { useChatConversations, type ChatConversationsController } from "./use-chat-conversations"

// A nudge-click seed should apply within one navigation; anything older is stale.
const COMPOSER_SEED_TTL_MS = 30_000

const KellyChatContext = createContext<ChatConversationsController | null>(null)

export interface KellyPanelValue {
  threads: Thread[]
  activeThreadId: string | null
  /** Terminal-state flag (success OR error) driving the preview's loading row — never hangs on error. */
  isThreadsLoaded: boolean
  isThreadsErrored: boolean
  selectThread: (threadId: string) => void
  startNewThread: () => void
}

// Exported so the preview's stories/tests can supply a controlled value without standing up the whole controller.
export const KellyPanelContext = createContext<KellyPanelValue | null>(null)

interface KellyChatProviderProps {
  children: ReactNode
  /** Streaming is the production default; stories pass "buffered" (see ADR 0007). */
  transportMode?: "streaming" | "buffered"
}

export function KellyChatProvider({ children, transportMode }: KellyChatProviderProps) {
  // The account's global selected risk model (or null), sent in agentArguments only when present (#4125) —
  // read here so the whole route shares one controller instance (index.tsx used to read it per surface).
  const modelId = useSelector(getModel)

  // Capture the pending seed once at mount, then clear it so a remount can't re-apply it. Seeds older
  // than the TTL (stranded by a navigation that never mounted this provider) are cleared but not applied.
  const pendingComposerSeed = useSelector(selectPendingComposerSeed)
  const [initialComposerDraft] = useState(() =>
    pendingComposerSeed && Date.now() - pendingComposerSeed.seededAt <= COMPOSER_SEED_TTL_MS
      ? pendingComposerSeed.text
      : null,
  )
  const dispatch = useDispatch()
  useEffect(() => {
    if (pendingComposerSeed) dispatch(consumeComposerSeed())
  }, [pendingComposerSeed, dispatch])

  const controller = useChatConversations({ modelId, transportMode, initialComposerDraft })

  const { threads, activeThreadId, isThreadsLoaded, isThreadsErrored, selectThread, startNewThread } = controller
  // Deps deliberately exclude messages/liveMessages so the rail never re-renders per streamed token.
  const panelValue = useMemo<KellyPanelValue>(
    () => ({ threads, activeThreadId, isThreadsLoaded, isThreadsErrored, selectThread, startNewThread }),
    [threads, activeThreadId, isThreadsLoaded, isThreadsErrored, selectThread, startNewThread],
  )

  return (
    <KellyChatContext.Provider value={controller}>
      <KellyPanelContext.Provider value={panelValue}>{children}</KellyPanelContext.Provider>
    </KellyChatContext.Provider>
  )
}

/** The full conversation controller — for the conversation surface. Throws outside the provider. */
export function useKellyChat(): ChatConversationsController {
  const context = useContext(KellyChatContext)
  if (context == null) throw new Error("useKellyChat must be used within a KellyChatProvider")
  return context
}

/** The panel-content slice — for the collapsed rail and its hover preview. Throws outside the provider. */
export function useKellyPanel(): KellyPanelValue {
  const context = useContext(KellyPanelContext)
  if (context == null) throw new Error("useKellyPanel must be used within a KellyChatProvider")
  return context
}

/** Wrap the route component so the provider mounts only once (after the feature + terms gates pass). */
export function withKellyChatProvider<Props extends object>(Component: ComponentType<Props>): ComponentType<Props> {
  function WithKellyChatProvider(props: Props) {
    // Keyed on the account: an in-place alias change would otherwise leave the previous account's draft on
    // screen, since the bridge's restore only fires when the new account's bucket holds one (ADR 0015).
    const accountAlias = useAccountAlias()
    return (
      <KellyChatProvider key={accountAlias}>
        <Component {...props} />
      </KellyChatProvider>
    )
  }
  WithKellyChatProvider.displayName = `withKellyChatProvider(${Component.displayName ?? Component.name ?? "Component"})`
  return WithKellyChatProvider
}
